import { UcoastEl } from '@/scripts/core/UcoastEl'
import {
	getAttributeOrThrow,
	getAttributeOrUndefined,
	qsaOptional,
	qsOptional,
	qsRequired,
} from '@/scripts/core/global'
import { type ProductForm } from '@/scripts/product-form-context/ProductForm'
import { type VariantRadios } from '@/scripts/product-form-context/VariantRadios'
import { type ProductSubscribe } from '@/scripts/product-form-context/ProductSubscribe'
import { type WaitlistPopup } from '@/scripts/product-form-context/WaitlistPopup'
import { type MediaGallery } from '@/scripts/product-form-context/MediaGallery'
import { type StickyCta } from '@/scripts/product-form-context/StickyCta'
import { type ProductFormStep } from '@/scripts/product-form-context/ProductFormStep'

type ProductOptions = {
	productForm: ProductForm
	variantRadios?: VariantRadios
	productSubscribe?: ProductSubscribe
	waitlistPopup?: WaitlistPopup
	mediaGallery?: MediaGallery
}
type UpsellSettings = {
	updateItemKey: string
	updatedItemQuantity: number
	startingPrice: number
}

// Mirror of `settings.site_wide_discount`, exposed by layout/theme.liquid as a
// multiplier (e.g. 13% off -> 0.87). Falls back to 1.0 (no discount) if absent.
const SITE_WIDE_DISCOUNT =
	typeof window !== 'undefined' &&
	typeof window.siteWideDiscountMultiplier === 'number'
		? window.siteWideDiscountMultiplier
		: 1.0

export class ProductFormContext extends UcoastEl {
	static htmlSelector = 'product-form-context'
	groups: ProductOptions[]
	productId: string
	uniqueId: string
	stickyCTA?: StickyCta
	productFormSteps?: NodeListOf<ProductFormStep>
	stepsCompleted = 0
	productVariants: ProductVariant[]
	optionNames?: string[]
	sellingPlanFrequency: number
	sellingPlanId?: number
	currentVariant: ProductVariant
	upsellSettings?: UpsellSettings

	constructor() {
		super()
		this.optionNames = getAttributeOrUndefined(
			'data-option-names',
			this
		)?.split(',')
		this.productId = getAttributeOrThrow('data-product-id', this)
		this.uniqueId = getAttributeOrThrow('data-unique-id', this)
		this.stickyCTA = qsOptional<StickyCta>(
			`sticky-cta[data-product-id="${this.productId}"][data-unique-id="${this.uniqueId}"]`
		)
		this.productFormSteps = qsaOptional<ProductFormStep>(
			`product-form-step[data-product-id="${this.productId}"][data-unique-id="${this.uniqueId}"]`
		)
		this.productVariants = this.getVariantData()
		// console.log('this.productVariants', this.productVariants)
		this.groups = this.getAllGroups()

		this.currentVariant = this.getCurrentVariant()
		if (this.hasAttribute('data-uc-update-item-key')) {
			this.upsellSettings = this.getUpsellSettings()
		}
		this.sellingPlanFrequency = this.getStartingSellingPlanFrequency()
		this.sellingPlanId = this.getDOMSellingPlanId()
		// Apply variant selection on init to ensure gallery matches URL-preselected variant
		if (this.currentVariant.options.length > 0) {
			this.applyVariantSelection(this.currentVariant.options)
		}
	}

	getUpsellSettings(): UpsellSettings {
		const updateItemKey = getAttributeOrThrow(
			'data-uc-update-item-key',
			this
		)
		const updatedItemQuantity = parseInt(
			getAttributeOrThrow('data-uc-updated-item-quantity', this)
		)
		const startingPrice = parseInt(
			getAttributeOrThrow('data-uc-starting-price', this)
		)
		return {
			updateItemKey,
			updatedItemQuantity,
			startingPrice,
		}
	}

	formatShopifyPrice(price: number): string {
		const priceToFormat = this.upsellSettings
			? price - this.upsellSettings.startingPrice
			: price
		return priceToFormat % 100 === 0
			? `$${(priceToFormat / 100).toFixed(0)}`
			: `$${(priceToFormat / 100).toFixed(2)}`
	}

	getStartingSellingPlanFrequency(): number {
		const firstSubscribeForm = this.groups[0]?.productSubscribe
		if (!firstSubscribeForm) return 0
		return parseInt(
			getAttributeOrThrow('data-starting-frequency', firstSubscribeForm)
		)
	}

	getCurrentVariant() {
		const currentVariant = this.productVariants.find(
			(variant) =>
				variant.id ===
				parseInt(this.groups[0].productForm.getVariantIdEl().value)
		)
		if (!currentVariant) throw new Error('currentVariant not found')
		return currentVariant
	}

	getVariantData(): ProductVariant[] {
		const json = qsRequired(
			'[type="application/json"][data-uc-context-variants]',
			this
		)?.textContent
		if (!json)
			throw new Error(
				`no json data found for ${this.productId}:${this.uniqueId}`
			)
		const initialVariantData = JSON.parse(json) as ProductVariant[]
		// special BFCM hardcoded stuff
		return initialVariantData.map((variant) => {
			return {
				...variant,
				compare_at_price: variant.compare_at_price ?? variant.price,
				price: variant.price * SITE_WIDE_DISCOUNT,
				selling_plan_allocations: variant.selling_plan_allocations.map(selling_plan_allocation => {
					return {
						...selling_plan_allocation,
						price: selling_plan_allocation.price * SITE_WIDE_DISCOUNT,
						// compare_at_price: selling_plan_allocation.price // enable this for site wide discount (last used 12.02.24)
					}
				})
			}
		});
	}

	getAllGroups(): ProductOptions[] {
		const groups: ProductOptions[] = []
		const locations = getAttributeOrThrow('data-locations', this).split(',')
		for (let i = 0; i < locations.length; i++) {
			const location = locations[i]
			const options = this.getProductOptions(
				this.productId,
				this.uniqueId,
				location
			)
			groups.push(options)
		}
		return groups
	}

	getProductOptions(
		productId: string,
		uniqueId: string,
		location: string
	): ProductOptions {
		const productForm = qsRequired<ProductForm>(
			`product-form[data-product-id="${productId}"][data-unique-id="${uniqueId}"][data-location="${location}"]`
		)

		const variantRadios = qsOptional<VariantRadios>(
			`variant-radios[data-product-id="${productId}"][data-unique-id="${uniqueId}"][data-location="${location}"]`
		)
		const productSubscribe = qsOptional<ProductSubscribe>(
			`product-subscribe[data-product-id="${productId}"][data-unique-id="${uniqueId}"][data-location="${location}"]`
		)

		const waitlistPopup = qsOptional<WaitlistPopup>(
			`waitlist-popup[data-product-id="${productId}"][data-unique-id="${uniqueId}"][data-location="${location}"]`
		)

		const mediaGallery = qsOptional<MediaGallery>(
			`media-gallery[data-product-id="${productId}"][data-unique-id="${uniqueId}"][data-location="${location}"]`
		)

		return {
			productForm,
			variantRadios,
			productSubscribe,
			waitlistPopup,
			mediaGallery,
		}
	}

	applyVariantSelection(selectedOptions: string[]) {
		const optionNames = this.optionNames ?? []
		const newVariant =
			this.productVariants.find((variant) => {
				return (
					variant.options.filter(
						(option: string, index: number) =>
							selectedOptions[index] === option
					).length === optionNames.length
				)
			}) ?? this.productVariants[0]

		// make some updates to the actual checkboxes that are checked
		this.groups.forEach((group) => {
			if (group.variantRadios) {
				selectedOptions.forEach((value: string, i: number) => {
					const optionName = optionNames[i]
					if (!optionName) return
					const optionRadios = qsaOptional<HTMLInputElement>(
						`input[type="radio"][name="${optionName}"]`,
						group.variantRadios
					)
					if (!optionRadios) return
					optionRadios.forEach((radio: HTMLInputElement) => {
						if (radio.value !== value) {
							radio.checked = false
							radio.setAttribute('checked', 'false')
						}
					})
					optionRadios.forEach((radio: HTMLInputElement) => {
						if (radio.value === value) {
							radio.checked = true
							radio.setAttribute('checked', '')
						}
					})
				})
			}

			if (!newVariant.available && group.waitlistPopup) {
				group.productForm.submitButton.classList.add('hidden')
				group.waitlistPopup.classList.remove('hidden')
				group.productSubscribe?.classList.add('hidden')
			} else if (group.waitlistPopup) {
				group.productForm.submitButton.classList.remove('hidden')
				group.waitlistPopup.classList.add('hidden')
				group.productSubscribe?.classList.remove('hidden')
			}

			if (group.mediaGallery) {
				const variantDependentMedia = qsaOptional(
					'[data-hide-unless-variant]',
					group.mediaGallery
				)
				if (variantDependentMedia) {
					let firstMediaFound = false
					variantDependentMedia.forEach((media) => {
						if (
							media.getAttribute('data-variant-id') ===
							`${newVariant.id}`
						) {
							media.setAttribute(
								'data-hide-unless-variant',
								'false'
							)
							if (!firstMediaFound) {
								media.classList.add('active', 'is-active')
								firstMediaFound = true
							} else {
								media.classList.remove('active', 'is-active')
							}
						} else {
							media.setAttribute(
								'data-hide-unless-variant',
								'true'
							)
							media.classList.remove('active', 'is-active')
						}
					})
				}
			}

			if (
				group.variantRadios &&
				group.variantRadios.colorLabels &&
				group.variantRadios.colorOption
			) {
				const colorOption = group.variantRadios.colorOption
				const colorLabels = group.variantRadios.colorLabels
				const color = newVariant[colorOption]
				if (color) {
					colorLabels.forEach((el) => {
						el.innerHTML = color
					})
				}
			}

			group.productForm.getVariantIdEl().value = `${newVariant.id}`

			let variantPrice = this.formatShopifyPrice(newVariant.price)

			if (this.groups[0]?.productSubscribe?.subscribeInput.checked) {
				const newSellingPlanId =
					newVariant.selling_plan_allocations[this.sellingPlanFrequency]
						?.selling_plan_id
				if (newSellingPlanId) {
					this.setSellingPlanId(newSellingPlanId)
				}
			}

			let displayPrice = variantPrice
			const sellingPlanId = this.getDOMSellingPlanId()
			const sellingPlanAllocation =
				newVariant.selling_plan_allocations[this.sellingPlanFrequency]

			if (
				sellingPlanId &&
				sellingPlanAllocation &&
				sellingPlanAllocation.price < sellingPlanAllocation.compare_at_price
			) {
				if (!sellingPlanAllocation) {
					throw new Error('sellingPlanAllocation not found')
				}
				const comparePrice = this.formatShopifyPrice(
					sellingPlanAllocation.compare_at_price
				)
				const realPrice = this.formatShopifyPrice(
					sellingPlanAllocation.price
				)
				displayPrice = `<s>${comparePrice}</s> ${realPrice}`
			} else if (newVariant.price < newVariant.compare_at_price) {
				if (!this.upsellSettings) {
					// Non-bundle: show original price struck through, discounted price current
					const comparePrice = this.formatShopifyPrice(
						newVariant.compare_at_price
					)
					const realPrice = this.formatShopifyPrice(newVariant.price)
					displayPrice = `<s>${comparePrice}</s> ${realPrice}`
				}
				// Bundle upsell: never show strikethrough — the upgrade cost is already
				// the meaningful number; a "was upgrade cost" is confusing to customers.
			}


			const updatedText = newVariant.available
				? `${group.productForm.addToCartText} – ${displayPrice}`
				: `${group.productForm.soldOutText}`
			if (group.productForm.ctaTextEl) {
				group.productForm.ctaTextEl.innerHTML = updatedText
			}
			group.productForm.variantIdEl.value = `${newVariant.id}`

			if (group.waitlistPopup && newVariant.sku) {
				const segmentKeyEls = qsaOptional(
					'[data-segment-key]',
					group.waitlistPopup
				)
				segmentKeyEls?.forEach((el) => {
					el.setAttribute(
						'data-segment-key',
						`Segment_oos_${newVariant.sku}`
					)
				})
			}
			if (group.productSubscribe) {
				// we have to update the price to the options
				group.productSubscribe.updateVariantFrequencyOptions(newVariant.id)
				const subPrice = newVariant.selling_plan_allocations.length > 0 ? newVariant.selling_plan_allocations[0].price : 0
				group.productSubscribe.updateVariantPrices(newVariant, this.formatShopifyPrice(newVariant.price), this.formatShopifyPrice(subPrice))
			}

			const priceOtpEl = qsOptional<HTMLElement>(
				`[data-price-otp][data-location="${group.productForm.getAttribute('data-location')}"]`
			)
			if (priceOtpEl) {
				priceOtpEl.innerHTML = variantPrice
			}
		})

		const variantChanged = this.currentVariant.id !== newVariant.id
		this.currentVariant = newVariant

		if (variantChanged) {
			const url = new URL(window.location.href)
			url.searchParams.set('variant', `${newVariant.id}`)
			window.history.replaceState({}, '', url.toString())
		}
	}

	getDOMSellingPlanId() {
		const subscribeInput = this.groups[0]?.productSubscribe?.subscribeInput

		if (subscribeInput?.value && subscribeInput.checked) {
			return parseInt(subscribeInput.value)
		} else {
			return undefined
		}
	}

	setSellingPlanId(id: number) {
		this.sellingPlanId = id
		this.groups.forEach((group) => {
			if (!group.productSubscribe) return
			group.productSubscribe.subscribeInput.value = `${id}`
		})
	}

	updateSellingPlanFrequency(frequency: number, displayText: string) {
		this.sellingPlanFrequency = frequency
		this.groups.forEach((group) => {
			if (!group.productSubscribe) return
			console.log({group: group.productSubscribe})
			const displayEl = qsRequired(
				'[skio-frequency-text]',
				group.productSubscribe
			)
			displayEl.innerHTML = displayText
		})

		const sellingPlanAllocation =
			this.currentVariant.selling_plan_allocations[frequency]
		if (!sellingPlanAllocation) return

		// stop here if we customer has chosen one-time
		if (!this.groups[0]?.productSubscribe?.subscribeInput.checked) {
			return
		}
		this.setSellingPlanId(sellingPlanAllocation.selling_plan_id)

		const selectedOptions =
			this.groups[0]?.variantRadios?.getSelectedOptions()
		if (selectedOptions) {
			// if we have selected options, we need to update the variant
			// the applyVariantSelection code already handles updating the UI
			this.applyVariantSelection(selectedOptions)
		} else {
			// otherwise we need to run the price update for *just* selling plan
			this.applyVariantSelection([])
		}
	}

	toggleSellingPlan(enable: boolean) {
		this.groups.forEach((group) => {
			if (!group.productSubscribe) return
			if (enable) {
				group.productSubscribe.oneTimeInput.checked = false
				group.productSubscribe.subscribeInput.checked = true
			} else {
				group.productSubscribe.oneTimeInput.checked = true
				group.productSubscribe.subscribeInput.checked = false
			}
		})
		// getDOMSellingPlanId doesnt get the class's selling plan id - it pulls it from an input element
		const DOMsellingPlanId = this.getDOMSellingPlanId()
		if (DOMsellingPlanId) {
			this.setSellingPlanId(DOMsellingPlanId)
		} else {
			this.sellingPlanId = undefined
		}

		const selectedOptions =
			this.groups[0]?.variantRadios?.getSelectedOptions()
		if (selectedOptions) {
			// if we have selected options, we need to update the variant
			// the applyVariantSelection code already handles updating the UI
			this.applyVariantSelection(selectedOptions)
		} else {
			// otherwise we need to run the price update for *just* selling plan
			this.applyVariantSelection([])
		}
	}

	initializeSteps() {
		if (!this.productFormSteps) return
		this.productFormSteps[0].classList.add('active')
	}
}


interface PriceAdjustment {
	position: number;
	price: number;
}

interface SellingPlanAllocation {
	price_adjustments: PriceAdjustment[];
	price: number;
	compare_at_price: number;
	per_delivery_price: number;
	selling_plan_id: number;
	selling_plan_group_id: string;
}

interface ProductVariant {
	id: number;
	title: string;
	option1: string | null;
	option2: string | null;
	option3: string | null;
	sku: string;
	requires_shipping: boolean;
	taxable: boolean;
	featured_image: string | null;
	available: boolean;
	name: string;
	public_title: string;
	options: string[];
	price: number;
	weight: number;
	compare_at_price: number;
	inventory_management: "shopify" | null;
	barcode: string;
	requires_selling_plan: boolean;
	selling_plan_allocations: SellingPlanAllocation[];
}
